import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const formatRemaining = (seconds) => {
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  }
  return `${seconds}s`;
};

export default function IdleWarningDialog({ open, secondsRemaining, warningSeconds, onStay, onSignOut }) {
  const progress = Math.max(0, Math.min(100, (secondsRemaining / warningSeconds) * 100));

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Do not allow backdrop / escape to dismiss — the user must explicitly
        // choose to stay or sign out. A silent dismiss would leave them
        // unsure whether the session is still alive.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      aria-labelledby="idle-warning-title"
      aria-describedby="idle-warning-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="idle-warning-title">Are you still there?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText id="idle-warning-description">
            For your security, you will be signed out due to inactivity in:
          </DialogContentText>
          <Typography variant="h4" align="center" color="warning.main">
            {formatRemaining(secondsRemaining)}
          </Typography>
          <LinearProgress variant="determinate" value={progress} color="warning" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onSignOut} color="inherit">
          Sign out
        </Button>
        <Button onClick={onStay} variant="contained" color="primary" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}

IdleWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  secondsRemaining: PropTypes.number.isRequired,
  warningSeconds: PropTypes.number.isRequired,
  onStay: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired
};
